import React__default from 'react';
import cn from 'classnames';
import { Icon } from '../Icon/Icon.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { Treeview } from '../Treeview/Treeview.js';
import { useDropTarget } from '../../utils/hooks/useDropTarget.js';

const Item = /*#__PURE__*/React__default.forwardRef(function Item(props, ref) {
  const {
    active,
    children,
    onDrop,
    postfix,
    prefix,
    role,
    ...otherProps
  } = props;
  const proxyRef = useProxiedRef(ref);
  const [isDraggedOver, dropTargetProps] = useDropTarget(onDrop);
  const isTreeitem = role === 'treeitem';
  const className = cn('yt-navigation__item cursor-pointer', {
    'w-full mb-px py-1 px-3 flex items-center hover:bg-grey-dark': isTreeitem,
    'bg-white w-full h-10 px-3 flex-shrink-0 flex items-center justify-between cursor-pointer': !isTreeitem,
    'yt-navigation__item--active': active && !isDraggedOver,
    'bg-grey-dark': isTreeitem && active && !isDraggedOver,
    'yt-navigation__item--dropping bg-blue': isDraggedOver
  }, props.className);

  const handleClick = event => {
    if (event.target instanceof HTMLAnchorElement || event.target instanceof HTMLButtonElement) {
      return;
    }

    if (proxyRef.current) {
      proxyRef.current.click();
    }
  };

  return React__default.createElement("span", Object.assign({}, dropTargetProps, {
    className: className,
    onClick: handleClick,
    role: role
  }), prefix && React__default.createElement("span", {
    className: "yt-navigation__item__prefix flex items-center"
  }, prefix), React__default.createElement("a", Object.assign({}, otherProps, {
    className: "block flex-grow truncate",
    ref: proxyRef
  }), children), postfix && React__default.createElement("span", {
    className: "yt-navigation__item__postfix ml-1"
  }, postfix));
});
const Panel = /*#__PURE__*/React__default.forwardRef(function Panel(props, ref) {
  return React__default.createElement("div", Object.assign({}, props, {
    className: cn('w-full bg-white p-3', props.className),
    ref: ref
  }));
});
const MenuGroup = /*#__PURE__*/React__default.forwardRef(function MenuGroup(props, ref) {
  const className = cn('flex-shrink-0 space-y-1 outline-none', {
    'bg-white pb-2': props.fixed
  }, props.className);

  const title = expanded => {
    const className = cn('bg-white h-10 pl-3 pr-1 flex items-center justify-between w-full', {
      'mb-1': expanded,
      'cursor-pointer hover:text-blue': !props.fixed
    });
    return React__default.createElement("span", {
      className: className
    }, typeof props.title === 'function' ? props.title(expanded) : props.title, !props.fixed && React__default.createElement(Icon, {
      name: expanded ? 'chevron-up' : 'chevron-down'
    }));
  };

  return React__default.createElement(Treeview.Group, Object.assign({}, props, {
    className: className,
    title: title,
    ref: ref
  }));
});
const Menu = /*#__PURE__*/React__default.forwardRef(function Menu(props, ref) {
  const scrollableAreas = React__default.useMemo(() => {
    const scrollableAreas = [];
    const children = React__default.Children.toArray(props.children).filter(child => !!child);
    children.forEach(child => {
      if (child.props.fixed) {
        scrollableAreas.push(child);
      } else {
        const x = scrollableAreas[scrollableAreas.length - 1];

        if (Array.isArray(x)) {
          x.push(child);
        } else {
          scrollableAreas.push([child]);
        }
      }
    });
    return scrollableAreas;
  }, [props.children]);
  return React__default.createElement(Treeview, Object.assign({}, props, {
    className: cn('divide-grey-light flex flex-grow flex-col divide-y-2 overflow-y-auto', props.className),
    ref: ref
  }), scrollableAreas.map((area, i) => Array.isArray(area) ? React__default.createElement("div", {
    className: "divide-grey-light flex h-0 flex-auto flex-shrink-0 flex-grow flex-col divide-y-2 overflow-y-auto",
    key: i
  }, area) : area));
});
Menu.Group = MenuGroup;
const Navigation = /*#__PURE__*/React__default.forwardRef(function Navigation(props, ref) {
  const {
    children,
    ...otherProps
  } = props;
  const className = cn('h-full flex flex-col bg-grey-light divide-y-2 divide-grey-light', props.className);
  return React__default.createElement("div", Object.assign({}, otherProps, {
    className: className,
    "data-taco": "navigation",
    ref: ref
  }), children);
});
Navigation.Menu = Menu;
Navigation.Item = Item;
Navigation.Panel = Panel;

export { Navigation };
//# sourceMappingURL=Navigation.js.map
